import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Avatar from '../ui/Avatar';

const SquadChatModal = ({ squad, currentUser, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [discordSettings, setDiscordSettings] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!squad) return;
    api.get(`/api/squads/${squad.id}/messages`).then(setMessages).catch(console.error).finally(() => setLoading(false));
    // Check if squad has Discord linked (for sync status display)
    api.get(`/api/integrations/discord/squad/${squad.id}`).then(setDiscordSettings).catch(() => {});
  }, [squad]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      // Post to app chat (will also sync to Discord if enabled in backend)
      const msg = await api.post(`/api/squads/${squad.id}/messages`, { message: newMessage.trim() });
      setMessages(prev => [...prev, { ...msg, username: currentUser.username, display_name: currentUser.display_name, theme_color: currentUser.theme_color, is_founder: currentUser.is_founder, founder_icon: currentUser.founder_icon, avatar_url: currentUser.avatar_url }]);
      setNewMessage('');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSending(false); }
  };

  const myColor = currentUser?.theme_color || THEME.omega;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '95%', maxWidth: '600px', height: '80vh', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${THEME.glassBorder}`, background: `linear-gradient(180deg, ${THEME.bgElevated}80 0%, transparent 100%)` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px' }}>{squad?.icon}</span>
              <div>
                <h3 style={{ margin: 0, color: THEME.text, fontSize: '18px', fontWeight: '700' }}>{squad?.name}</h3>
                <p style={{ margin: 0, color: THEME.textMuted, fontSize: '12px' }}>
                  Squad Chat
                  {discordSettings?.sync_chat && <span style={{ color: '#5865F2', marginLeft: '8px' }}>synced to Discord</span>}
                </p>
              </div>
            </div>
            <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</motion.button>
          </div>
        </div>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={24} color={myColor} /></div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: THEME.textMuted }}>No messages yet. Start the conversation!</div>
          ) : (
            messages.map((msg, i) => {
              const isMe = msg.username === currentUser?.username;
              return (
                <div key={msg.id || i} style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                  <Avatar src={msg.avatar_url} name={msg.display_name} size={32} color={msg.theme_color || THEME.text} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ color: msg.theme_color || THEME.text, fontWeight: '600', fontSize: '13px' }}>
                        {msg.is_founder && `${msg.founder_icon || '⭐'} `}{msg.display_name}
                        {msg.from_discord && <span style={{ color: '#5865F2', marginLeft: '4px', fontSize: '10px' }}>via Discord</span>}
                      </span>
                      <span style={{ color: THEME.textDim, fontSize: '11px' }}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div style={{ background: isMe ? myColor : THEME.bg, color: isMe ? '#000' : THEME.text, padding: '10px 14px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px', fontSize: '14px', wordBreak: 'break-word' }}>
                      {msg.message}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <div style={{ padding: '16px', borderTop: `1px solid ${THEME.border}`, display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={discordSettings?.sync_chat ? "Type a message (synced to Discord)..." : "Type a message..."}
            style={{ flex: 1, background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px 16px', color: THEME.text, fontSize: '14px', outline: 'none' }}
          />
          <button onClick={handleSend} disabled={sending || !newMessage.trim()} style={{
            background: myColor, color: '#000', border: 'none', borderRadius: '10px', padding: '12px 20px',
            fontWeight: '600', cursor: (sending || !newMessage.trim()) ? 'not-allowed' : 'pointer', opacity: (sending || !newMessage.trim()) ? 0.5 : 1
          }}>{sending ? '...' : 'Send'}</button>
        </div>
      </motion.div>
    </div>
  );
};

export default SquadChatModal;
